/* eslint-disable indent */
import ErrorPage from 'next/error'
import { useRouter } from 'next/router'
import T from 'prop-types'
import React from 'react'

import { topicQuery } from '@/api'
import { getImageUrl } from '@/api/sanity'
import useColor from '@/hooks/useColor'
import { useFocusSimple as useFocus } from '@/hooks/useFocus'
import { buildGetStaticPaths, buildGetStaticProps } from '@/lib/page'

import { IntroductionEntry } from '@/components/Introduction'
import LessonEntry from '@/components/LessonEntry'
import NavList from '@/components/NavList'
import SanityPreview from '@/components/SanityPreview'
import SEO from '@/components/SEO'
import TopicCover from '@/components/TopicCover'

import ArticleEntries from '@/components/ArticleEntries'
import LearningAidsEntry from '@/components/LearningAidsEntry'
import Section from '@/components/Section'
import VideoEntries from '@/components/VideoEntries'

const query = `
  *[_type == "topic" && slug.current == $slug]${topicQuery} | order(_updatedAt desc)[0]
`

function InnerTopicPage({ topic, status }) {
  const { illustration } = useColor(topic, status)
  const lessons = [
    ...((topic?.lessonOrder ?? []).map((id) => topic?.lessons?.find(({ _id }) => _id === id)) ||
      []),
    ...(topic?.lessons?.filter(({ _id }) => (topic?.lessonOrder ?? [])?.indexOf(_id) === -1) ?? []),
  ].map((item) => ({
    ...item,
    topic: { slug: topic.slug },
    topicTheme: topic?.topicTheme,
  }))

  const introduction = topic?.introduction

  const subTopics = topic?.resources.filter((item) => item._type === 'subTopic') ?? []
  const videos = topic?.resources.filter((item) => item._type === 'video') ?? []
  const resources = topic?.resources.filter((item) => item._type === 'resource') ?? []
  const talkingTypes = topic?.resources.filter((item) => item._type === 'talkingType') ?? []
  const quiz = topic?.resources.filter((item) => item._type === 'quiz') ?? []
  const presentations = topic?.resources.filter((item) => item._type === 'presentation') ?? []
  const articles = topic?.resources.filter((item) => item._type === 'article') ?? []

  const menu = [
    { _key: 'introduction', title: 'Introduksjon' },
    { _key: 'lessons', title: 'Undervisningsopplegg' },
    { _key: 'subTopics', title: 'Samlinger', hidden: !(subTopics.length > 0) },
    { _key: 'videos', title: 'Videoer', hidden: !(videos.length > 0) },
    { _key: 'resources', title: 'Hjelpemidler', hidden: !(resources.length > 0) },
    { _key: 'talkingTypes', title: 'Samtalekort', hidden: !(talkingTypes.length > 0) },
    { _key: 'quiz', title: 'Quiz', hidden: !(quiz.length > 0) },
    { _key: 'presentations', title: 'Presentasjoner', hidden: !(presentations.length > 0) },
    { _key: 'articles', title: 'Artikler', hidden: !(articles.length > 0) },
  ].filter((item) => !item.hidden)

  const active = useFocus(menu.map(({ _key }) => _key))

  return (
    <>
      <SEO
        title={topic?.title}
        description={topic?.preamble}
        path={`tema/${topic?.slug}`}
        image={topic?.ogImages?.facebook && getImageUrl(topic?.ogImages?.facebook).url()}
      />

      <NavList active={active} inline items={menu} back={{ title: 'Hjem', href: '/' }} />

      <div id="introduction">
        <TopicCover {...topic} illustration={illustration} />

        {introduction && <IntroductionEntry {...introduction} topic={topic} slug={topic?.slug} />}
      </div>

      <Section title="Undervisningsopplegg" id="lessons" type="list">
        <LessonEntry items={lessons} />
      </Section>

      {subTopics.length > 0 && (
        <Section title="Samlinger" id="subTopics" type="list">
          <ArticleEntries
            urlBuilder={(slug) => `/samling/${slug}`}
            promoted
            articles={subTopics.map((item) => ({
              ...item,
              _type: 'staticPage',
              _id: item.objectID,
            }))}
          />
        </Section>
      )}

      {videos?.length > 0 && (
        <Section title="Video" id="videos" type="list">
          <VideoEntries items={videos} />
        </Section>
      )}

      {resources?.length > 0 && (
        <Section title="Hjelpemidler" id="resources" type="list">
          <LearningAidsEntry
            items={resources.map((item) => ({
              ...item,
            }))}
          />
        </Section>
      )}

      {talkingTypes?.length > 0 && (
        <Section title="Samtalekort" id="talkingTypes" type="list">
          <LearningAidsEntry
            items={talkingTypes.map((item) => ({
              ...item,
            }))}
          />
        </Section>
      )}

      {quiz?.length > 0 && (
        <Section title="Quiz" id="quiz" type="list">
          <LearningAidsEntry
            items={quiz.map((item) => ({
              ...item,
            }))}
          />
        </Section>
      )}

      {presentations?.length > 0 && (
        <Section title="Presentasjoner" id="presentations" type="list">
          <LearningAidsEntry
            items={presentations.map((item) => ({
              ...item,
            }))}
          />
        </Section>
      )}

      {articles?.length > 0 && (
        <Section title="Artikler" id="articles" type="list">
          <ArticleEntries
            urlBuilder={(slug) => `/artikkel/${slug}`}
            articles={articles.map((item) => ({
              ...item,
              _type: 'article',
              _id: item.objectID,
            }))}
          />
        </Section>
      )}
    </>
  )
}

export default function TopicPage({ preview, pageData, error, status, slug }) {
  const router = useRouter()

  if (!router.isFallback && !pageData) {
    return <ErrorPage statusCode={404} />
  }

  if (error) {
    return <ErrorPage statusCode={error.statusCode} title={error.title} />
  }

  if (typeof window !== 'undefined') {
    window.router = router
  }

  return (
    <SanityPreview preview={preview} initialData={pageData} query={query} params={{ slug }}>
      {(topic) => {
        return <InnerTopicPage topic={topic} status={status} />
      }}
    </SanityPreview>
  )
}

TopicPage.propTypes = {
  status: T.string,
  pageData: T.shape({
    topic: T.shape({
      _id: T.string,
      slug: T.string,
      title: T.string,
      preamble: T.string,
      promotedLesson: T.object,
      lessons: T.array,
    }),
  }).isRequired,
  slug: T.string.isRequired,
  preview: T.bool.isRequired,
  error: T.shape({
    statusCode: T.number.isRequired,
    title: T.string.isRequired,
  }),
}

export const getStaticProps = buildGetStaticProps(query)

export const getStaticPaths = buildGetStaticPaths('topic')
